/**
 * Session Recovery
 *
 * A cached Web3Auth session that SOKA no longer accepts must not lock the user
 * out permanently. This module classifies a failed `/auth/register` call and,
 * only when SOKA rejected the credential itself, clears the Web3Auth session
 * and the persisted auth state so the next reload does not restore the same
 * dead token.
 */

import web3authGateway from './web3auth.gateway';
import { useAuthStore } from '../stores/useAuthStore';

/** The subset of shapes a register failure can carry a status on. */
interface StatusBearingError {
  response?: { status?: number };
  status?: number;
}

/**
 * True when `error` represents SOKA refusing the credential itself (401/403),
 * never a transient failure (5xx, network failure, timeout) — those must stay
 * retryable. Recognises both call sites' error shapes: the axios error raised
 * by `AuthContext`'s register call (`error.response.status`), and the
 * status-carrying `Error` thrown by the bridge's register fetch
 * (`error.status`).
 */
export function isCredentialRejection(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { response, status } = error as StatusBearingError;
  const httpStatus = response?.status ?? status;

  return httpStatus === 401 || httpStatus === 403;
}

/**
 * Clears the Web3Auth session and the persisted auth state after a credential
 * rejection ended a login attempt, leaving nothing restorable on the next
 * reload. Tolerates a throwing logout (v9 today, v11 after story 33-04) — a
 * failure here must never replace the register error that triggered it.
 */
export async function clearRejectedSession(): Promise<void> {
  try {
    await web3authGateway.logout();
  } catch {
    // A failed Web3Auth logout must not mask the original register error.
  }

  try {
    useAuthStore.getState().logout();
  } catch {
    // Same rule for clearing the persisted auth state.
  }
}
